/**
 * Every piece of user-facing wording, in one place.
 *
 * Components read from here rather than holding literals, so copy can be
 * reviewed and changed without touching behaviour, and so adding a second
 * language later is a matter of swapping this module rather than editing
 * every component. Anything that varies with the data is a function.
 */
import type { Currency, DistanceUnit, FuelType, ItemPeriod, TimePeriod } from "./types";

export const APP_NAME = "CarCost";

/** Split so the logo can colour the second half. */
export const WORDMARK = { start: "Car", end: "Cost" } as const;

export const PERIOD_LABELS: Record<TimePeriod, string> = {
  year: "Year",
  month: "Month",
  week: "Week",
  day: "Day",
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = { GBP: "£", EUR: "€" };
/** Pence and cents. */
export const CURRENCY_MINOR: Record<Currency, string> = { GBP: "p", EUR: "c" };
export const CURRENCY_LABELS: Record<Currency, string> = { GBP: "Pounds", EUR: "Euros" };
export const UNIT_LABELS: Record<DistanceUnit, string> = { mi: "Miles", km: "Kilometres" };
/** Short forms for the Mi/Km toggle and the per-distance period option. */
export const UNIT_SHORT: Record<DistanceUnit, string> = { mi: "Mi", km: "Km" };
/** How the economy figure is expressed in each unit. */
export const ECONOMY_UNITS: Record<DistanceUnit, string> = { mi: "mpg", km: "km/L" };
/** "Fuel" for a combustion car, "Energy" for an electric one. */
export const FUEL_NOUN: Record<FuelType, string> = { ice: "Fuel", ev: "Energy" };

export const header = {
  tagline:
    "Fixed ownership costs plus fuel or energy, split by how much of your driving is charged or fuelled at home versus away.",
  currencyGroup: "Currency",
  currencyHint: "Only changes the symbol; amounts are not converted.",
  unitGroup: "Distance unit",
  unitHint: "Converts all distances and economy figures; results stay the same.",
  skipToContent: "Skip to main content",
};

export const sections = {
  profiles: "Driving profiles",
  vehicles: "Vehicles",
  compare: "Compare",
  summary: "Summary",
  fixedCosts: "Fixed costs",
  fuelCosts: "Fuel costs",
  distance: "Distance",
  split: "Home / away split",
};

export const tabs = {
  addProfile: "Add profile",
  addVehicle: "Add vehicle",
  remove: "Remove",
  removeNamed: (name: string) => `Remove ${name}`,
  /** Shown in place of a name the person has cleared. */
  untitled: "Untitled",
};

export const profilePanel = {
  nameLabel: "Profile name",
  namePlaceholder: "e.g. Weekday commute",
  totalPrefix: "Total",
  perLabel: "per",
  distanceLabel: "Total distance",
  periodLabel: "Distance period",
  home: "Home",
  away: "Away",
  sliderLabel: "Share of distance fuelled or charged at home",
  sliderValueText: (home: string, away: string) => `Home ${home}, away ${away}`,
  /** Fallback when a profile's name is empty. */
  fallbackName: "Profile",
};

export const vehicleForm = {
  nameLabel: "Vehicle name",
  namePlaceholder: "e.g. My car",
  exampleTag: "Example",
  reviewNote: "Dashed fields are starting estimates. Replace them with your own figures.",
  costNameLabel: "Cost name",
  costNamePlaceholder: "e.g. Insurance",
  amountFor: (name: string) => `Amount for ${name || "this cost"}`,
  periodFor: (name: string) => `Period for ${name || "this cost"}`,
  removeCost: "Remove cost",
  removeCostNamed: (name: string) => `Remove ${name || "cost"}`,
  addCost: "Add a cost",
  perLabel: "per",
  subTotal: "Sub total",
  fuelTypeLabel: "Fuel type",
  fuelOptions: { ice: "Fossil", ev: "Electric" } as Record<FuelType, string>,
  homePrice: "Home unit price",
  awayPrice: "Away unit price",
  economy: "Economy",
  /** Fallback when a vehicle's name is empty. */
  fallbackName: "This vehicle",
};

export const results = {
  totalPerUnit: (unit: DistanceUnit) => `Total cost per ${unit}`,
  annualTotal: "Annual total",
  legendFixed: "Fixed (your costs)",
  legendHome: (pct: number, unit: DistanceUnit) => `Home rate (${pct}% of ${unit})`,
  legendAway: (pct: number, unit: DistanceUnit) => `Away rate (${pct}% of ${unit})`,
  tableCaption: "Cost breakdown per unit of distance",
  columnComponent: "Component",
  columnPerUnit: (unit: DistanceUnit) => `Per ${unit}`,
  rowFixed: "Fixed costs",
  rowHome: (fuel: FuelType) => `${FUEL_NOUN[fuel]} at home rate`,
  rowAway: (fuel: FuelType) => `${FUEL_NOUN[fuel]} at away rate`,
  rowBlended: (fuel: FuelType) => `${FUEL_NOUN[fuel]} blended at your split`,
  rowTotal: "Total",
  /** The stacked bar repeats the table above it, so it is hidden from assistive tech. */
  barAlternative: "The same figures are listed in the breakdown table below.",
};

export const compare = {
  intro: "Every vehicle under each driving profile, cheapest first. Bars share one scale, so you can compare across profiles too.",
  cheapest: "Cheapest",
  fixedPrefix: "Fixed",
  groupLabel: (profileName: string) => `Vehicles for ${profileName}`,
};

export const help = {
  fixedCosts: {
    label: "How fixed costs are calculated",
    body: (unit: DistanceUnit) =>
      `Your fixed costs added up per year, then divided by the yearly distance of the selected driving profile. Costs entered per ${unit === "km" ? "kilometre" : "mile"} apply directly.`,
  },
  fuelCosts: {
    label: "How fuel costs are calculated",
    body: (fuel: FuelType, unit: DistanceUnit) => {
      if (fuel === "ev") return "Price per kWh ÷ economy, blended by the profile's home/away split.";
      return unit === "mi"
        ? "Price per litre × 4.54609 ÷ mpg, blended by the profile's home/away split."
        : "Price per litre ÷ km/L, blended by the profile's home/away split.";
    },
  },
  total: {
    label: "How the total is calculated",
    body: (fuel: FuelType, unit: DistanceUnit) =>
      `Fixed cost per ${unit} plus ${FUEL_NOUN[fuel].toLowerCase()} cost per ${unit}. Fixed costs are spread over the profile's yearly distance.`,
  },
  blended: {
    label: "What blended means",
    body: "The home and away rates weighted by how much of your driving is done at each.",
  },
};

export const numberInput = {
  reviewTitle: "Check this figure and enter your own value",
  emptyTitle: "Enter a value greater than 0",
};

export const theme = {
  labels: { auto: "Auto", light: "Light", dark: "Dark" },
  title: (current: string, next: string) => `Theme: ${current} (click for ${next})`,
  ariaLabel: (current: string, next: string) => `Theme: ${current}. Switch to ${next}`,
};

export const transfer = {
  groupLabel: "Save and load",
  exportButton: "Export",
  importButton: "Import",
  importHeading: "Import",
  close: "Close",
  ok: "OK",
  cancel: "Cancel",
  replace: "Replace mine",
  add: "Add to mine",
  chooseFile: "Choose a file…",
  hint: "Open a file you exported earlier. You'll be asked whether to replace your vehicles and profiles or add these alongside them.",
  previewLabel: "Imported file",
  fileSource: (name: string) => `From ${name}`,
  /** The downloaded file is named carcost-YYYY-MM-DD.json. */
  fileName: (isoDate: string) => `carcost-${isoDate}.json`,
  errors: {
    emptyInput: "Choose a file first.",
    newer: "This was made with a newer version of the calculator, so it can't be read here.",
    notOurs: `That doesn't look like a ${APP_NAME} export file.`,
    tooLarge: `That file is too large to be a ${APP_NAME} export.`,
    downloadFailed: "Couldn't save a file here.",
  },
  matchesNote: (n: number) =>
    ` ${n} of these already ${n === 1 ? "matches" : "match"} something you have, and will be updated in place rather than duplicated if you add them.`,
  unitNote: (toUnit: DistanceUnit) =>
    ` Distances and economy figures will be converted to ${toUnit === "km" ? "kilometres" : "miles"} if you add them to yours.`,
  currencyNote: (incoming: string, mine: string) =>
    ` Its amounts are in ${incoming}: "Add to mine" keeps your ${mine} setting and doesn't convert money.`,
  loaded: (what: string) => `Loaded ${what}.`,
  nothingNew: "Nothing new, already up to date.",
  added: (n: number) => `${plural(n, "item")} added`,
  updated: (n: number) => `${plural(n, "item")} updated`,
};

export const footerNote =
  "Figures are estimates for comparison, not a guarantee of what you'll spend. Nothing is sent to a server: your data stays in this browser, and Export saves it to a file you can Import later or on another device.";

// ---------- Default data names ----------
// Seeded into new records, so they are data rather than chrome, but still copy.

export const defaultNames = {
  newVehicle: "New vehicle",
  newProfile: (n: number) => `Profile ${n}`,
  examplePetrol: "Example: petrol hatchback",
  exampleElectric: "Example: electric hatchback",
  exampleProfiles: ["Commuter", "Long distance"],
  costs: {
    finance: "Finance",
    service: "Service",
    mot: "MOT",
    roadTax: "Road Tax",
    roadTaxPerDistance: "Road Tax Per Mile",
    insurance: "Insurance",
  },
};

// ---------- Helpers ----------

/** "1 item" / "2 items". */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** Label for one option of the cost-item period select. */
export function itemPeriodLabel(period: ItemPeriod, unit: DistanceUnit): string {
  return period === "distance" ? UNIT_SHORT[unit] : PERIOD_LABELS[period];
}
